/**
 * adminDirectory.ts — de accountlijst van het adminportaal, van de server.
 *
 * WAAROM DIT ER NIET AL WAS
 *
 * Het adminportaal las zijn gebruikers uit localStorage. Dat was geen
 * slordigheid maar een gevolg: `profiles_select` (0001) laat een admin alleen
 * zijn eigen profiel zien, plus leerlingen met een actief break-glass-venster.
 * Een gewone `select * from profiles` levert een beheerder dus nul rijen op.
 * Er was letterlijk geen serverbron om uit te lezen.
 *
 * Migratie 0025 voegt die bron toe, en trekt daarbij de grens die break glass
 * bedoelt: BEHEREN (bestaat dit account, welke rol, nog actief) mag zonder
 * venster; INZIEN (hoe doet dit kind het) niet. Deze module levert alleen het
 * eerste. Er komt geen enkel voortgangsgetal doorheen, en leerlingen zijn
 * gepseudonimiseerd.
 *
 * WAT DIT NIET IS
 *
 * Geen vervanging van de lokale store. Die draagt nog steeds de leerlingdata
 * en de relaties. Deze lijst zegt wat er op de SERVER staat — en juist het
 * verschil tussen die twee is wat een beheerder nu voor het eerst kan zien.
 */

import { supabase } from './client';
import { error as logError } from '../../utils/logger';
import { pseudonymFor } from '../../portals/shared/learnerPseudonym';
export interface DirectoryAccount {
  id: string;
  role: 'learner' | 'parent' | 'teacher' | 'admin';
  /** Naam bij volwassenen, pseudoniem (L-A3F91) bij leerlingen. */
  label: string;
  isPseudonymous: boolean;
  email: string | null;
  username: string | null;
  usernameNamespace: string | null;
  grade: number | null;
  dataController: string | null;
  consentStatus: string | null;
  teacherStatus: string | null;
  /**
   * Alleen bij leerkrachten; `null` betekent solo (geen schoolbinding).
   *
   * Dit is het gegeven waarop een beheerder verifieert. Zonder dit keurde hij
   * een naam goed zonder te weten namens welke school — zie migratie 0038.
   */
  schoolName: string | null;
  /**
   * Waar die schoolnaam vandaan komt (migratie 0041). Dit is het gegeven waarop
   * een beheerder verifieert:
   *
   *   'registry' → gekozen uit ons register. Natrekbaar.
   *   'request'  → zelf ingetypt. Een bewering.
   *   'none'     → geen school; zelfstandige leerkracht.
   *
   * Zonder dit veld zijn een keuze en een aanvraag niet te onderscheiden, en is
   * goedkeuren een handeling die eruitziet als een controle.
   */
  schoolSource: 'registry' | 'request' | 'none' | null;
  createdAt: string | null;
  lastActiveAt: string | null;
  deletionWarningAt: string | null;
  /** Heeft de huidige beheerder nu een venster op deze leerling? */
  hasAccess: boolean;
}
export interface DirectoryResult {
  ok: boolean;
  message?: string;
  accounts: DirectoryAccount[];
}
interface DirectoryRow {
  id: string;
  role: string;
  label: string;
  is_pseudonymous: boolean;
  email: string | null;
  username: string | null;
  username_namespace: string | null;
  grade: number | null;
  data_controller: string | null;
  consent_status: string | null;
  teacher_status: string | null;
  /** Ontbreekt zolang 0038 niet gedraaid is — vandaar de `?? null` hieronder. */
  school_name?: string | null;
  created_at: string | null;
  last_active_at: string | null;
  deletion_warning_at: string | null;
  has_access: boolean;
}
export async function listAccounts(): Promise<DirectoryResult> {
  const {
    data,
    error
  } = await supabase.rpc('admin_list_accounts');
  if (error) {
    logError('[AdminDirectory] Lijst ophalen mislukt', error);
    return {
      ok: false,
      message: 'De accountlijst van de server kon niet worden opgehaald. Je ziet nu alleen wat er op dit apparaat staat.',
      accounts: []
    };
  }
  const rows = (data ?? []) as DirectoryRow[];
  return {
    ok: true,
    accounts: rows.map(r => ({
      id: r.id,
      role: r.role as DirectoryAccount['role'],
      // Bij een gepseudonimiseerde rij leiden we het label hier af in plaats van
      // de serverwaarde te tonen. De server rekent met SHA-256, het
      // activiteitenlog met een rolling hash — hetzelfde kind kreeg daardoor in
      // twee schermen een ánder pseudoniem, en dat is precies de eigenschap die
      // een pseudoniem moet leveren. Eén client-side bron lost dat op; de server
      // blijft zijn label sturen als garantie dat er nooit een naam over de lijn
      // gaat. Namen van volwassenen komen ongewijzigd door.
      label: r.is_pseudonymous ? pseudonymFor(r.id) : r.label,
      isPseudonymous: r.is_pseudonymous,
      email: r.email,
      username: r.username,
      usernameNamespace: r.username_namespace,
      grade: r.grade,
      dataController: r.data_controller,
      consentStatus: r.consent_status,
      teacherStatus: r.teacher_status,
      schoolName: r.school_name ?? null,
      // Ontbreekt het veld, dan draait 0041 nog niet. Dan liever `null` — "niet
      // bekend" — dan een gok als 'request': dat zou een gekozen school als
      // bewering presenteren en een beheerder onnodig laten twijfelen.
      schoolSource: (r as {
        school_source?: 'registry' | 'request' | 'none' | null;
      }).school_source ?? null,
      createdAt: r.created_at,
      lastActiveAt: r.last_active_at,
      deletionWarningAt: r.deletion_warning_at,
      hasAccess: r.has_access
    }))
  };
}